/**
 * Hourly-island: de "Komende uren"-strook op desktop door-bladerbaar maken. De strook is een
 * horizontaal scrollbare lijst; op mobiel swipe je 'm, maar met een muis kun je niet makkelijk
 * horizontaal scrollen — dan zag je alleen het eerste stuk. Daarom injecteert dit island twee
 * klik-pijltjes (eerdere/latere uren). Zonder JS zouden dat dode knoppen zijn, dus rendert de
 * server ze niet (zelfde patroon als de alert-sluitknop); de CSS toont ze alléén op een
 * muis-apparaat (hover + fine pointer).
 *
 * Progressive enhancement: zonder JS blijft de strook scrollbaar (dunne scrollbalk) en staan alle
 * 24 uur gewoon in de DOM. Toegankelijk: echte <button>s met Nederlandse labels, aria-controls naar
 * de strook, de focus blijft behouden als een pijltje verdwijnt (springt naar de andere knop i.p.v.
 * naar <body>), en het scrollen respecteert prefers-reduced-motion.
 */

// Omlaag-chevron (zelfde pad als de UI-chevron); de CSS draait 'm naar links/rechts. aria-hidden:
// de knop draagt het label, het icoon is decoratief. Client-only → niet via de server-iconen.
const CHEVRON =
  '<svg class="pw-hr-chevron" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9.5 L12 15.5 L18 9.5"/></svg>';

/**
 * Scroll-staat → welke pijltjes nodig zijn. `eps` vangt subpixel-afronding op (scrollWidth en
 * clientWidth ronden los af). Past de hele strook al in beeld → geen pijltjes.
 */
export function navState(
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number,
  eps = 2,
): { showPrev: boolean; showNext: boolean } {
  const max = scrollWidth - clientWidth;
  if (max <= eps) return { showPrev: false, showNext: false };
  return { showPrev: scrollLeft > eps, showNext: scrollLeft < max - eps };
}

/** Tijdens sleep-scrollen: de nieuwe scrollLeft uit de startpositie en de muisverplaatsing. */
export function dragScrollLeft(startScroll: number, startX: number, currentX: number): number {
  return startScroll - (currentX - startX);
}

let stripSeq = 0;

/** Eén "Komende uren"-strook verrijken met eerdere/latere-knoppen. */
function enhance(wrap: HTMLElement): void {
  const strip = wrap.querySelector<HTMLElement>('.pw-hr-strip');
  if (!strip || wrap.querySelector('.pw-hr-nav')) return; // geen strook, of al verrijkt

  const reduce =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  // De strook een id geven zodat de knoppen er via aria-controls naar kunnen wijzen.
  if (!strip.id) strip.id = `pw-hr-strip-${++stripSeq}`;

  const mkBtn = (cls: string, label: string): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `pw-hr-nav ${cls}`;
    b.setAttribute('aria-label', label);
    b.setAttribute('aria-controls', strip.id);
    b.innerHTML = CHEVRON;
    b.hidden = true; // pas tonen zodra er in die richting te scrollen valt (zie update)
    return b;
  };
  const prev = mkBtn('pw-hr-prev', 'Eerdere uren');
  const next = mkBtn('pw-hr-next', 'Latere uren');
  strip.before(prev); // vóór/ná de strook → logische tab-volgorde: eerdere · lijst · latere
  strip.after(next);
  wrap.classList.add('is-nav');

  const update = (): void => {
    const { showPrev, showNext } = navState(strip.scrollLeft, strip.scrollWidth, strip.clientWidth);
    // Focus niet verliezen: verplaats 'm van een knop die we zo verbergen naar de andere (nog
    // zichtbare) knop, anders valt de focus terug naar <body>.
    if (!showPrev && showNext && document.activeElement === prev) next.focus();
    else if (!showNext && showPrev && document.activeElement === next) prev.focus();
    prev.hidden = !showPrev;
    next.hidden = !showNext;
  };

  const page = (dir: 1 | -1): void => {
    // ~80% van de zichtbare breedte per klik (een paar uur), met wat overlap voor context.
    const amount = Math.max(strip.clientWidth * 0.8, 120);
    strip.scrollBy({ left: dir * amount, behavior: reduce ? 'auto' : 'smooth' });
  };

  prev.addEventListener('click', () => page(-1));
  next.addEventListener('click', () => page(1));
  strip.addEventListener('scroll', update, { passive: true });
  addEventListener('resize', update);

  // Sleep-scrollen met de muis: grijp de strook en trek 'm naar links/rechts. Alléén voor de muis
  // (touch swipet al via native scroll). Bewust GÉÉN setPointerCapture — dat bleek het programmatische
  // scrollen op de scroll-container te blokkeren; in plaats daarvan luisteren we naar move/up op
  // `window`, zodat het slepen doorloopt als de cursor de strook verlaat. preventDefault op pointerdown
  // + user-select:none (CSS) houden de tekstselectie tegen.
  let dragging = false;
  let dragStartX = 0;
  let dragStartScroll = 0;
  strip.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return; // alleen muis, linkerknop
    dragging = true;
    dragStartX = e.clientX;
    dragStartScroll = strip.scrollLeft;
    strip.classList.add('is-dragging');
    e.preventDefault(); // voorkom dat de browser een tekstselectie start
  });
  const onMove = (e: PointerEvent): void => {
    if (!dragging) return;
    strip.scrollLeft = dragScrollLeft(dragStartScroll, dragStartX, e.clientX);
  };
  const endDrag = (): void => {
    if (!dragging) return;
    dragging = false;
    strip.classList.remove('is-dragging');
  };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', endDrag);
  window.addEventListener('pointercancel', endDrag);

  update();
}

/** Verrijk elke "Komende uren"-strook op de pagina (no-op zonder strook). */
export function initHourly(): void {
  for (const wrap of document.querySelectorAll<HTMLElement>('.pw-hr-wrap')) enhance(wrap);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHourly);
} else {
  initHourly();
}
